import { Injectable } from '@angular/core';
import { SortOrderEnum } from '@core/shared/enums/sort-order.enum';
import {
  ADD_USER_ROLE,
  CREATE_USER,
  DELETE_USER,
  GET_USER,
  GET_USERS,
  REMOVE_USER_ROLE,
  UPDATE_USER,
} from '@queries/users.graphql';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { UserCreateInput, UserType, UserUpdateInput } from './users.types';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private _item$: BehaviorSubject<UserType | null> = new BehaviorSubject(null);
  private _items$: BehaviorSubject<UserType[] | null> = new BehaviorSubject(null);

  constructor(private readonly apollo: Apollo) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get item$(): Observable<UserType | null> {
    return this._item$.asObservable();
  }

  get items$(): Observable<UserType[] | null> {
    return this._items$.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Loads every user into items$. Signature and default variables are
   * unchanged from before the Users screen existed — InitialDataResolver,
   * SelectedCreatedByComponent (via items$) and DialogSalesTargetComponent
   * depend on them. Search is done client-side by the Users list (same as
   * payment-conditions), never here, so this shared list is never narrowed
   * for those other consumers.
   */
  getUsers(skip: number = 0, take: number = 10000): Observable<UserType[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ UserFindAll: UserType[] }>({
        query: GET_USERS,
        variables: {
          skip,
          take,
          orderBy: [{ nome: SortOrderEnum.asc }],
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          this._items$.next(data.UserFindAll);
          return of(data.UserFindAll);
        }),
      );
  }

  /**
   * Loads one user into item$. A falsy id (the `new` route) yields an active
   * blank record without a network call — same contract as
   * PaymentConditionService.getPaymentCondition({}).
   */
  getUser(id: number): Observable<UserType> {
    if (!id) {
      const blank: UserType = {
        id: '',
        username: '',
        nome: '',
        email: '',
        isActive: true,
        roles: [],
      };
      this._item$.next(blank);
      return of(blank);
    }

    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ UserFindOne: UserType }>({
        query: GET_USER,
        variables: { where: { id } },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          this._item$.next(data.UserFindOne);
          return of(data.UserFindOne);
        }),
      );
  }

  /**
   * `active` is UserCreate's separate top-level argument (isActive is
   * @HideField on UserCreateInput). It is omitted entirely when not given so
   * the backend's own default (true) applies.
   */
  createUser(data: UserCreateInput, active?: boolean): Observable<UserType> {
    const variables = active === undefined ? { data } : { data, active };
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ UserCreate: UserType }>({
        mutation: CREATE_USER,
        variables,
      })
      .pipe(
        switchMap(({ data: result }) => {
          this._item$.next(result.UserCreate);
          return of(result.UserCreate);
        }),
      );
  }

  updateUser(id: number, data: UserUpdateInput): Observable<UserType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ UserUpdate: UserType }>({
        mutation: UPDATE_USER,
        variables: { where: { id }, data },
      })
      .pipe(
        switchMap(({ data: result }) => {
          this._item$.next(result.UserUpdate);
          return of(result.UserUpdate);
        }),
      );
  }

  /**
   * Not wired to any button in Phase 1: UserDelete fails with an FK
   * violation for any user linked to Roles2Users/Permissions2Users/
   * SalesTarget (no onDelete cascade in the backend schema).
   */
  deleteUser(id: number): Observable<UserType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ UserDelete: UserType }>({
        mutation: DELETE_USER,
        variables: { where: { id } },
      })
      .pipe(switchMap(({ data: result }) => of(result.UserDelete)));
  }

  /**
   * Deliberately does NOT push into item$: the detail form patches itself
   * from item$, so re-emitting here would wipe the admin's unsaved edits to
   * username/nome/email on every role checkbox click. The caller reads the
   * role set from the returned user instead.
   */
  addRole(userId: number, roleId: number): Observable<UserType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ UserAddRole: UserType }>({
        mutation: ADD_USER_ROLE,
        variables: { userId, roleId },
      })
      .pipe(switchMap(({ data: result }) => of(result.UserAddRole)));
  }

  /** Same item$ caveat as addRole(). */
  removeRole(userId: number, roleId: number): Observable<UserType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ UserRemoveRole: UserType }>({
        mutation: REMOVE_USER_ROLE,
        variables: { userId, roleId },
      })
      .pipe(switchMap(({ data: result }) => of(result.UserRemoveRole)));
  }
}
